import { useState, useEffect } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authApi, storiesApi } from '../api/client';
import { useAuth } from '../context/AuthContext';
import StoryCard from '../components/StoryCard';
import SEO, { SITE_URL } from '../components/SEO';

const TABS = [
  { key: 'stories', label: 'Published' },
  { key: 'drafts',  label: 'Drafts' },
  { key: 'bookmarks', label: '🔖 Bookmarks' },
];

export default function Profile() {
  const { username } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();

  const tab = searchParams.get('tab') || 'stories';
  const isOwn = currentUser?.username === username;

  const [profile, setProfile] = useState(null);
  const [stories, setStories] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [following, setFollowing] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    authApi.userDetail(username).then(r => {
      setProfile(r.data);
      setFollowing(r.data.is_following || false);
      setFollowerCount(r.data.follower_count || 0);
      setEditForm({
        first_name: r.data.first_name || '',
        last_name: r.data.last_name || '',
        bio: r.data.bio || '',
        country: r.data.country || '',
        website: r.data.website || '',
        twitter: r.data.twitter || '',
      });
    }).catch(() => navigate('/stories')).finally(() => setLoading(false));
  }, [username, navigate]);

  useEffect(() => {
    if (!profile) return;
    if (tab === 'stories') {
      storiesApi.list({ author: username }).then(r => setStories(r.data.results || r.data));
    } else if (tab === 'drafts' && isOwn) {
      storiesApi.myStories().then(r => {
        const all = r.data.results || r.data;
        setDrafts(all.filter(s => s.status === 'draft'));
      });
    } else if (tab === 'bookmarks' && isOwn) {
      storiesApi.bookmarked().then(r => setBookmarks(r.data));
    }
  }, [tab, profile, username, isOwn]);

  const handleFollow = async () => {
    if (!currentUser) return navigate('/login');
    try {
      const res = await authApi.followToggle(username);
      setFollowing(res.data.following);
      setFollowerCount(res.data.follower_count);
    } catch { /* ignore */ }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(editForm).forEach(([k, v]) => fd.append(k, v));
      if (avatarFile) fd.append('avatar', avatarFile);
      await authApi.updateProfile(fd);
      // Refresh profile
      const r = await authApi.userDetail(username);
      setProfile(r.data);
      setEditMode(false);
    } catch (err) {
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="loading-text">Loading profile…</div>;
  if (!profile) return null;

  const displayName = profile.first_name
    ? `${profile.first_name} ${profile.last_name}`.trim()
    : profile.username;
  const initials = (profile.first_name?.[0] || profile.username[0]).toUpperCase();

  const visibleTabs = isOwn ? TABS : TABS.filter(t => t.key === 'stories');
  const tabContent = {
    stories: stories,
    drafts: drafts,
    bookmarks: bookmarks,
  }[tab] || [];

  const profileJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Person',
      name: displayName,
      identifier: profile.username,
      description: profile.bio || `${displayName} on Pitch & Pen`,
      url: `${SITE_URL}/profile/${profile.username}`,
      image: profile.avatar || undefined,
      ...(profile.country && { addressCountry: profile.country }),
      ...(profile.website && { sameAs: [profile.website] }),
    },
  };

  return (
    <div>
      <SEO
        title={`${displayName} (@${profile.username})`}
        description={
          profile.bio
            ? profile.bio.slice(0, 155)
            : `Read stories and follow ${displayName} on Pitch & Pen — African football's storytelling platform.`
        }
        image={profile.avatar}
        url={`${SITE_URL}/profile/${profile.username}`}
        type="profile"
        jsonLd={profileJsonLd}
      />
      {/* ── Profile header ─────────────────────── */}
      <div className="profile-header">
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', flexWrap: 'wrap' }}>
            {/* Avatar */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              {(avatarPreview || profile.avatar)
                ? <img src={avatarPreview || profile.avatar} alt={displayName} className="profile-avatar" />
                : <div className="profile-avatar-fallback">{initials}</div>
              }
              {isOwn && editMode && (
                <label style={{
                  position: 'absolute', bottom: 0, right: 0, width: 28, height: 28,
                  background: 'rgba(0,0,0,0.6)', borderRadius: '50%', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.85rem',
                }}>
                  📷
                  <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { setAvatarFile(f); setAvatarPreview(URL.createObjectURL(f)); }
                  }} />
                </label>
              )}
            </div>

            {/* Info */}
            <div style={{ flex: 1, minWidth: 200 }}>
              {editMode ? (
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <input
                    value={editForm.first_name} onChange={e => setEditForm(f => ({ ...f, first_name: e.target.value }))}
                    placeholder="First name"
                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0.4rem 0.7rem', color: 'white', fontFamily: 'var(--font-serif)', fontSize: '1.1rem', flex: 1 }}
                  />
                  <input
                    value={editForm.last_name} onChange={e => setEditForm(f => ({ ...f, last_name: e.target.value }))}
                    placeholder="Last name"
                    style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0.4rem 0.7rem', color: 'white', fontFamily: 'var(--font-serif)', fontSize: '1.1rem', flex: 1 }}
                  />
                </div>
              ) : (
                <h1 style={{ fontSize: '1.75rem', marginBottom: '0.2rem' }}>{displayName}</h1>
              )}
              <div style={{ fontFamily: 'var(--font-sans)', fontSize: '0.85rem', opacity: 0.7, marginBottom: '0.6rem' }}>
                @{profile.username}
                {profile.role && <span style={{ marginLeft: '0.5rem', background: 'rgba(255,255,255,0.15)', padding: '2px 8px', borderRadius: 12, fontSize: '0.75rem' }}>{profile.role}</span>}
                {profile.country && <span style={{ marginLeft: '0.5rem' }}>· {profile.country}</span>}
              </div>

              {editMode ? (
                <textarea
                  value={editForm.bio} onChange={e => setEditForm(f => ({ ...f, bio: e.target.value }))}
                  placeholder="Your bio…" rows={2}
                  style={{ width: '100%', maxWidth: 480, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0.5rem 0.75rem', color: 'white', fontSize: '0.9rem', resize: 'vertical' }}
                />
              ) : profile.bio ? (
                <p style={{ fontSize: '0.9rem', opacity: 0.85, lineHeight: 1.65, maxWidth: 480 }}>{profile.bio}</p>
              ) : null}

              {/* Stats */}
              <div style={{ display: 'flex', gap: '2rem', marginTop: '1rem' }}>
                <div className="profile-stat">
                  <span className="profile-stat-n">{profile.story_count || 0}</span>
                  <span className="profile-stat-l">Stories</span>
                </div>
                <div className="profile-stat">
                  <span className="profile-stat-n">{followerCount}</span>
                  <span className="profile-stat-l">Followers</span>
                </div>
                <div className="profile-stat">
                  <span className="profile-stat-n">{profile.following_count || 0}</span>
                  <span className="profile-stat-l">Following</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignSelf: 'flex-start', marginTop: '0.5rem' }}>
              {isOwn ? (
                editMode ? (
                  <>
                    <button onClick={handleSaveProfile} disabled={saving} className="btn btn-sm" style={{ background: 'white', color: 'var(--green)' }}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                    <button onClick={() => setEditMode(false)} className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setEditMode(true)} className="btn btn-sm" style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)' }}>
                      Edit profile
                    </button>
                    <Link to="/write" className="btn btn-sm" style={{ background: 'white', color: 'var(--green)' }}>
                      ✏️ Write
                    </Link>
                  </>
                )
              ) : (
                currentUser && (
                  <button onClick={handleFollow} className="btn btn-sm" style={{
                    background: following ? 'rgba(255,255,255,0.15)' : 'white',
                    color: following ? 'white' : 'var(--green)',
                    border: following ? '1px solid rgba(255,255,255,0.3)' : 'none',
                  }}>
                    {following ? '✓ Following' : '+ Follow'}
                  </button>
                )
              )}
            </div>
          </div>

          {/* Edit extra fields */}
          {isOwn && editMode && (
            <div style={{ marginTop: '1.5rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', maxWidth: 640 }}>
              {[
                { key: 'country', placeholder: 'Country' },
                { key: 'website', placeholder: 'Website URL' },
                { key: 'twitter', placeholder: 'Twitter handle (no @)' },
              ].map(f => (
                <input key={f.key} value={editForm[f.key]} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.3)', borderRadius: 6, padding: '0.45rem 0.75rem', color: 'white', fontFamily: 'var(--font-sans)', fontSize: '0.875rem' }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Tabs + content ─────────────────────── */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', position: 'sticky', top: 'var(--nav-h)', zIndex: 50 }}>
        <div className="container">
          <div style={{ display: 'flex', gap: 0 }}>
            {visibleTabs.map(t => (
              <button key={t.key}
                onClick={() => setSearchParams({ tab: t.key })}
                style={{
                  padding: '0.85rem 1.25rem', fontFamily: 'var(--font-sans)', fontSize: '0.875rem',
                  fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer',
                  borderBottom: tab === t.key ? '2px solid var(--green)' : '2px solid transparent',
                  color: tab === t.key ? 'var(--green)' : 'var(--text-muted)',
                  transition: 'color 0.12s',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ padding: '2.5rem 0' }}>
        <div className="container">
          {tabContent.length === 0 ? (
            <div className="empty-state">
              {tab === 'stories' && (
                <>
                  <h3>No published stories yet</h3>
                  {isOwn && <Link to="/write" className="btn btn-primary" style={{ marginTop: '1rem' }}>Write your first story</Link>}
                </>
              )}
              {tab === 'drafts' && <h3>No drafts saved</h3>}
              {tab === 'bookmarks' && <h3>No bookmarked stories</h3>}
            </div>
          ) : (
            <div className="stories-grid">
              {tabContent.map(s => <StoryCard key={s.id} story={s} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
